import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  AppBar,
  Toolbar,
  Button,
  Typography,
  Box,
  Link,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme,
  Popper,
  Paper,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const Navbar = ({
  hideLoginButton = false,
  enableScrollEffect = true,
  customStyles = {},
}) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [featuresMegaMenuOpen, setFeaturesMegaMenuOpen] = useState(false);
  const [featuresAnchorEl, setFeaturesAnchorEl] = useState(null);
  const navigate = useNavigate();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  useEffect(() => {
    if (!enableScrollEffect) return;

    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [enableScrollEffect]);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const navItems = ['Features', 'Pricing', 'Documentation', 'Reviews', 'Contact'];

  const handleNavClick = (item) => {
    if (item === 'Pricing') {
      // Check if we're already on the main page
      if (window.location.pathname === '/') {
        // We're on the main page, just scroll to pricing
        const pricingSection = document.getElementById('pricing');
        if (pricingSection) {
          pricingSection.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      } else {
        // We're on a different page, navigate to main page first, then scroll
        navigate('/');
        setTimeout(() => {
          const pricingSection = document.getElementById('pricing');
          if (pricingSection) {
            pricingSection.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          }
        }, 100); // Small delay to ensure page has loaded
      }
    } else if (item === 'Documentation') {
      navigate('/documentation');
      setTimeout(() => {
        try {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (_) {
          window.scrollTo(0, 0);
        }
      }, 0);
    } else if (item === 'Reviews') {
      navigate('/reviews');
    } else if (item === 'Contact') {
      navigate('/contact');
    }
  };

  const getAppBarStyles = () => ({
    backgroundColor:
      enableScrollEffect && scrolled ? 'rgba(15, 28, 54, 0.5)' : '#3044A9',
    backdropFilter:
      enableScrollEffect && scrolled ? 'blur(20px)' : 'blur(10px)',
    transition: 'all 0.3s ease',
    boxShadow:
      enableScrollEffect && scrolled ? '0 8px 32px rgba(0, 0, 0, 0.1)' : 'none',
    border:
      enableScrollEffect && scrolled
        ? '1px solid rgba(255, 255, 255, 0.1)'
        : '1px solid rgba(255, 255, 255, 0.05)',
    ...customStyles.appBar,
  });

  const getToolbarStyles = () => ({
    justifyContent: 'space-between',
    background: 'transparent',
    backdropFilter: 'blur(15px)',
    px: { xs: 2, sm: 3, md: 4 },
    py: { xs: 1.5, sm: 2 },
    ...customStyles.toolbar,
  });

  return (
    <>
      <AppBar position="fixed" sx={getAppBarStyles()}>
        <Toolbar sx={getToolbarStyles()}>
          {/* Brand */}
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: '#ffffff',
              cursor: 'pointer',
              fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
              textShadow: '0 2px 4px rgba(0,0,0,0.3)',
              letterSpacing: '0.5px',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'scale(1.02)',
                textShadow: '0 4px 8px rgba(0,0,0,0.4)',
              },
              ...customStyles.brand,
            }}
            onClick={() => navigate('/')}
          >
            Home
          </Typography>

          {/* Desktop Navigation - Centered */}
          {!isMobile && (
            <Box
              sx={{
                display: 'flex',
                gap: { md: 4, lg: 5 },
                alignItems: 'center',
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                ...customStyles.navigationBox,
              }}
            >
              {navItems.map((item) => {
                if (item === 'Features') {
                  return (
                    <Box
                      key={item}
                      sx={{ position: 'relative' }}
                      onMouseEnter={(event) => {
                        setFeaturesAnchorEl(event.currentTarget);
                        setFeaturesMegaMenuOpen(true);
                      }}
                      onMouseLeave={() => {
                        setFeaturesMegaMenuOpen(false);
                        setFeaturesAnchorEl(null);
                      }}
                    >
                      <Link
                        variant="body1"
                        sx={{
                          color: 'rgba(255,255,255,0.9)',
                          textTransform: 'none',
                          textDecoration: 'none',
                          cursor: 'pointer',
                          fontWeight: 500,
                          fontSize: { md: '0.9rem', lg: '1rem' },
                          letterSpacing: '0.3px',
                          position: 'relative',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            color: '#ffffff',
                            transform: 'translateY(-1px)',
                            textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                          },
                          '&:after': {
                            content: '""',
                            position: 'absolute',
                            width: featuresMegaMenuOpen ? '100%' : 0,
                            height: '2px',
                            bottom: '-4px',
                            left: '50%',
                            background:
                              'linear-gradient(90deg, #3b82f6, #60a5fa)',
                            transition: 'all 0.3s ease',
                            transform: 'translateX(-50%)',
                          },
                          '&:hover:after': {
                            width: '100%',
                          },
                          ...customStyles.navLink,
                        }}
                      >
                        {item}
                      </Link>
                    </Box>
                  );
                }

                return (
                  <Link
                    key={item}
                    variant="body1"
                    onClick={() => handleNavClick(item)}
                    sx={{
                      color: 'rgba(255,255,255,0.9)',
                      textTransform: 'none',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      fontWeight: 500,
                      fontSize: { md: '0.9rem', lg: '1rem' },
                      letterSpacing: '0.3px',
                      position: 'relative',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        color: '#ffffff',
                        transform: 'translateY(-1px)',
                        textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                      },
                      '&:after': {
                        content: '""',
                        position: 'absolute',
                        width: 0,
                        height: '2px',
                        bottom: '-4px',
                        left: '50%',
                        background: 'linear-gradient(90deg, #3b82f6, #60a5fa)',
                        transition: 'all 0.3s ease',
                        transform: 'translateX(-50%)',
                      },
                      '&:hover:after': {
                        width: '100%',
                      },
                      ...customStyles.navLink,
                    }}
                  >
                    {item}
                  </Link>
                );
              })}
            </Box>
          )}

          {/* Features Mega Menu */}
          {/* Features Mega Menu */}
          <Popper
            open={featuresMegaMenuOpen}
            anchorEl={featuresAnchorEl}
            placement="bottom-start"
            sx={{ zIndex: 1300 }}
            modifiers={[
              {
                name: 'offset',
                options: {
                  offset: [0, 8],
                },
              },
            ]}
          >
            <Paper
              sx={{
                borderRadius: '16px',
                boxShadow: '0 18px 45px rgba(15, 23, 42, 0.35)',
                border: '1px solid rgba(148, 163, 184, 0.4)',
                minWidth: { xs: '100%', sm: '640px', md: '720px' },
                maxWidth: '800px',
                overflow: 'hidden',

                // 🔹 soft transparent gradient background
                background:
                  'linear-gradient(135deg, rgba(96, 155, 249, 0.18), rgba(112, 172, 246, 0.06))',
                backdropFilter: 'blur(16px)',
              }}
              onMouseEnter={() => setFeaturesMegaMenuOpen(true)}
              onMouseLeave={() => {
                setFeaturesMegaMenuOpen(false);
                setFeaturesAnchorEl(null);
              }}
            >
              <Box sx={{ p: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: '#ffffff',
                    mb: 1,
                    fontSize: '1.1rem',
                  }}
                >
                  Product Features
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#e5efff',
                    opacity: 0.9,
                    mb: 2.5,
                    fontSize: '0.9rem',
                  }}
                >
                  Explore what you can do with SkyShift
                </Typography>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                    gap: 1.5,
                    maxWidth: '800px',
                  }}
                >
                  {[
                    'Interactive Dashboards',
                    'User Specific Projects',
                    'Secured Connections',
                    'Cloud-to-Cloud Transfer',
                    'Cloud to On-Premise',
                    'Schedule Data Transfer',
                    'Auto Field Mapping',
                    'Status & Debugging',
                    'Support Available',
                  ].map((feature, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        p: 1.5,
                        borderRadius: '10px',
                        transition: 'all 0.18s ease',
                        border: '1px solid rgba(148,163,184,0.45)',

                        // glassy card over gradient
                        backgroundColor: 'rgba(255,255,255,0.86)',

                        '&:hover': {
                          bgcolor: 'rgba(255,255,255,0.98)',
                          borderColor: '#3b82f6',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 6px 18px rgba(59, 130, 246, 0.18)',
                        },
                      }}
                    >
                      <CheckCircleIcon
                        sx={{
                          color: '#4c63d2',
                          fontSize: '1rem',
                          mt: '1px',
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#0f172a',
                          fontSize: '0.8rem',
                          lineHeight: 1.3,
                          fontWeight: 500,
                        }}
                      >
                        {feature}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Paper>
          </Popper>

          {/* Desktop Login and Signup Buttons - Right */}
          {!isMobile && !hideLoginButton && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/trial')}
                sx={{
                  ml: 2,
                  fontWeight: 600,
                  fontSize: { md: '0.85rem', lg: '0.95rem' },
                  color: '#ffffff',
                  letterSpacing: '0.5px',
                  textTransform: 'none',
                  transition: 'transform 0.3s ease, color 0.3s ease',
                  animation: 'pulseAnim 1.5s infinite ease-in-out',

                  '&:hover': {
                    color: '#ffffff',
                    transform: 'scale(1.2)',
                    border: '1px solid #ffffff',
                  },

                  '@keyframes pulseAnim': {
                    '0%': { transform: 'scale(1)' },
                    '50%': { transform: 'scale(1.07)' },
                    '100%': { transform: 'scale(1)' },
                  },
                }}
              >
                Get a Demo
              </Button>
              <Button
                variant="outlined"
                sx={{
                  borderColor: '#ffffff',
                  color: '#ffffff',
                  borderRadius: '50px',
                  padding: { md: '0.4rem 1rem', lg: '0.5rem 1rem' },
                  fontWeight: 600,
                  fontSize: { md: '0.85rem', lg: '0.9rem' },
                  letterSpacing: '0.5px',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: '#ffffff',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    transform: 'translateY(-2px) scale(1.02)',
                  },
                  ...customStyles.signupButton,
                }}
                onClick={() => navigate('/signup')}
              >
                Sign Up
              </Button>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: '#ffffff',
                  color: '#0f1c36',
                  borderRadius: '50px',
                  padding: { md: '0.4rem 1.2rem', lg: '0.5rem 1.5rem' },
                  fontWeight: 600,
                  fontSize: { md: '0.85rem', lg: '0.9rem' },
                  letterSpacing: '0.5px',
                  boxShadow: '0 4px 16px rgba(255,255,255,0.2)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: '#f0f0f0',
                    transform: 'translateY(-2px) scale(1.02)',
                    boxShadow: '0 8px 25px rgba(255,255,255,0.3)',
                  },
                  ...customStyles.loginButton,
                }}
                onClick={() => navigate('/login')}
              >
                Login
              </Button>
            </Box>
          )}

          {/* Mobile Menu Icon */}
          {isMobile && (
            <IconButton
              sx={{
                color: '#ffffff',
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.2)',
                '&:hover': {
                  background: 'rgba(255,255,255,0.2)',
                },
              }}
              onClick={handleDrawerToggle}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      <Drawer anchor="right" open={mobileOpen} onClose={handleDrawerToggle}>
        <Box
          sx={{
            width: 250,
            py: 2,
            background: 'linear-gradient(135deg, #0f1c36 0%, #1a2a63 100%)',
            height: '100%',
            color: '#ffffff',
          }}
        >
          <List>
            {navItems.map((item) => (
              <ListItem
                button
                key={item}
                onClick={() => {
                  handleNavClick(item);
                  handleDrawerToggle();
                }}
                sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.1)',
                  },
                }}
              >
                <ListItemText
                  primary={item}
                  sx={{
                    '& .MuiListItemText-primary': {
                      color: '#ffffff',
                      fontWeight: 500,
                    },
                  }}
                />
              </ListItem>
            ))}
            {!hideLoginButton && (
              <>
                <ListItem
                  button
                  onClick={() => {
                    navigate('/signup');
                    handleDrawerToggle();
                  }}
                  sx={{
                    mt: 1,
                    mx: 2,
                    borderRadius: '10px',
                    backgroundColor: 'transparent',
                    border: '1px solid #ffffff',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                    },
                  }}
                >
                  <ListItemText
                    primary="Sign Up"
                    sx={{
                      textAlign: 'center',
                      '& .MuiListItemText-primary': {
                        color: '#ffffff',
                        fontWeight: 600,
                      },
                    }}
                  />
                </ListItem>
                <ListItem
                  button
                  onClick={() => {
                    navigate('/login');
                    handleDrawerToggle();
                  }}
                  sx={{
                    mt: 1,
                    mx: 2,
                    borderRadius: '10px',
                    backgroundColor: '#ffffff',
                    '&:hover': {
                      backgroundColor: '#f0f0f0',
                    },
                  }}
                >
                  <ListItemText
                    primary="Login"
                    sx={{
                      textAlign: 'center',
                      '& .MuiListItemText-primary': {
                        color: '#0f1c36',
                        fontWeight: 600,
                      },
                    }}
                  />
                </ListItem>
              </>
            )}
          </List>
        </Box>
      </Drawer>
    </>
  );
};

Navbar.propTypes = {
  hideLoginButton: PropTypes.bool,
  enableScrollEffect: PropTypes.bool,
  customStyles: PropTypes.object,
};

export default Navbar;
